import React, { useRef, useMemo, useCallback, useState } from "react";
import axios from "axios";
import JoditEditor from "jodit-react";

import { BASE_URL } from "../../Const/Url";
import { showToast } from "../../../utils/ToastHelper";

function Editor({ content, setContent }) {
  const editor = useRef(null);
  const fileInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);

  const insertImageToEditor = useCallback(
    (imageUrl) => {
      if (!imageUrl) {
        showToast("error", "Image URL missing from upload response");
        return;
      }

      const imageHtml = `<p><img src="${imageUrl}" alt="uploaded" /></p>`;

      try {
        const jodit = editor.current;
        const selection = jodit?.editor?.s;

        // Preferred: insert at current cursor/selection if available
        if (selection?.insertHTML) {
          try {
            selection.insertHTML(imageHtml);
            const updated = jodit.value || "";
            setContent(updated);
            return;
          } catch (e) {
            // fall through to append mode
            console.error("insertHTML at cursor failed, will append", e);
          }
        }

        // Fallback: append to the end of current content
        if (jodit) {
          const newContent = (jodit.value || "") + imageHtml;
          jodit.value = newContent;
          setContent(newContent);
          return;
        }
      } catch (err) {
        console.error(
          "Failed to insert image via editor APIs, fallback to state",
          err
        );
      }

      // Final fallback: update state directly
      setContent((prev = "") => prev + imageHtml);
    },
    [setContent]
  );

  const uploadDocument = useCallback(
    (file) => {
      if (!file) return;
      setIsUploading(true);
      const formData = new FormData();
      formData.append("document", file);
      formData.append("doc_type", 0);

      axios
        .post(`${BASE_URL}api/v1/auth/documents/upload/`, formData)
        .then((res) => {
          if (!res?.data?.status) {
            showToast("error", "Failed to upload file");
            return;
          }
          const data = res?.data?.data || {};
          const imageUrl =
            data?.resized_doc_url ||
            data?.doc_url ||
            data?.document ||
            data?.resize_document ||
            "";

          showToast("success", "File uploaded successfully");
          insertImageToEditor(imageUrl);
        })
        .catch((err) => {
          const message =
            err?.response?.data?.errors?.document?.[0] ||
            err?.response?.data?.message ||
            "Upload failed";
          showToast("error", message);
        })
        .finally(() => {
          setIsUploading(false);
        });
    },
    [insertImageToEditor]
  );

  const config = useMemo(
    () => ({
      readonly: false,
      placeholder: "Start typing...",
      buttons: [
        "bold",
        "italic",
        "underline",
        "|",
        "ul",
        "ol",
        "|",
        "font",
        "fontsize",
        "brush",
        "|",
        "image",
        "link",
        // "|",
        // "insertTable",
        // "|",
        "align",
        "undo",
        "redo",
      ],
      height: 400,
      uploader: {
        insertImageAsBase64URI: false,
      },
      removeButtons: [],
      showXPathInStatusbar: false,
      extraButtons: {
        insertTable: {
          tooltip: "Insert Table",
          text: "Table",
          exec: function (editor) {
            const rows = prompt("Number of rows:", "3");
            const cols = prompt("Number of columns:", "3");

            if (rows && cols) {
              try {
                const rowCount = parseInt(rows) || 3;
                const colCount = parseInt(cols) || 3;

                let tableHTML =
                  "<table style='border-collapse: collapse; width: 100%; margin: 10px 0;'><tbody>";
                for (let i = 0; i < rowCount; i++) {
                  tableHTML += "<tr>";
                  for (let j = 0; j < colCount; j++) {
                    tableHTML +=
                      "<td style='border: 1px solid #ddd; padding: 8px;'>&nbsp;</td>";
                  }
                  tableHTML += "</tr>";
                }
                tableHTML += "</tbody></table>";

                // Use a safer approach: update editor value directly
                try {
                  const currentContent = editor.value || "";
                  const newContent = currentContent + tableHTML;
                  editor.value = newContent;

                  // Trigger change event
                  if (editor.events) {
                    editor.events.fire("change");
                  }
                } catch (err) {
                  console.error("Error in toolbar button:", err);
                }
              } catch (error) {
                console.error("Error inserting table:", error);
                alert("Error inserting table. Please try again.");
              }
            }
          },
        },
      },
    }),
    []
  );

  const handleBlur = useCallback(
    (newContent) => {
      setContent(newContent);
    },
    [setContent]
  );

  const handleChange = useCallback(
    (newContent) => {
      setContent(newContent);
    },
    [setContent]
  );

  const insertTableManually = useCallback(() => {
    if (editor.current) {
      const jodit = editor.current;
      const rows = prompt("Number of rows:", "3");
      const cols = prompt("Number of columns:", "3");

      if (rows && cols) {
        try {
          const rowCount = parseInt(rows) || 3;
          const colCount = parseInt(cols) || 3;

          let tableHTML =
            "<table style='border-collapse: collapse; width: 100%; margin: 10px 0;'><tbody>";
          for (let i = 0; i < rowCount; i++) {
            tableHTML += "<tr>";
            for (let j = 0; j < colCount; j++) {
              tableHTML +=
                "<td style='border: 1px solid #ddd; padding: 8px;'>&nbsp;</td>";
            }
            tableHTML += "</tr>";
          }
          tableHTML += "</tbody></table>";

          // Use a safer approach: update content state and let Jodit handle it
          if (jodit) {
            try {
              // Get current content directly from editor
              const currentContent = jodit.value || "";

              // Insert table at the end of content
              const newContent = currentContent + tableHTML;

              // Update editor value
              jodit.value = newContent;

              // Update state (onChange will be triggered automatically)
              setContent(newContent);

              // Focus the editor
              if (jodit.editor) {
                jodit.editor.focus();
              }
            } catch (err) {
              console.error("Error inserting table:", err);
              // Fallback: append to content state
              setContent((prev) => prev + tableHTML);
            }
          } else {
            // Fallback: append to content
            setContent((prev) => prev + tableHTML);
          }
        } catch (error) {
          console.error("Error inserting table:", error);
          alert("Error inserting table. Please try again.");
        }
      }
    }
  }, [setContent]);

  const triggerImageUpload = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);

  const handleFileInputChange = useCallback(
    (event) => {
      const file = event.target.files?.[0];
      if (file) {
        uploadDocument(file);
      }
      // reset input so same file can be re-selected
      event.target.value = "";
    },
    [uploadDocument]
  );

  return (
    <div>
      <div
        className="d-flex justify-content-end gap-2"
        style={{ marginBottom: "10px" }}
      >
        <input
          type="file"
          accept="image/*"
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={handleFileInputChange}
        />
        <button
          onClick={triggerImageUpload}
          className="btn btn-primary btn-sm"
          type="button"
          disabled={isUploading}
        >
          {isUploading ? "Uploading..." : "Upload Image"}
        </button>
        <button
          onClick={insertTableManually}
          className="btn btn-secondary btn-sm"
          type="button"
        >
          Insert Table
        </button>
      </div>
      <JoditEditor
        ref={editor}
        value={content}
        config={config}
        tabIndex={1}
        onBlur={handleBlur}
        onChange={handleChange}
        onError={(error) => {
          console.error("Jodit Editor Error:", error);
        }}
      />
    </div>
  );
}

export default Editor;
